import { useEffect, useMemo, useState } from "react";
import { Award } from "lucide-react";
import api from "../lib/api";

const MENU_POSTER_FALLBACK = "https://customer-assets.emergentagent.com/job_cheers-book/artifacts/18tn9hch_ChatGPT%20Image%2019%20juin%202026%20a%CC%80%2012_37_48.png";

const CATS = [
  { id: "all", label: "Tout" },
  { id: "mocktail", label: "Mocktails" },
  { id: "calvados", label: "Calvados" },
  { id: "rhum", label: "Cocktails au Rhum" },
  { id: "rhum_arrange", label: "Signatures" },
  { id: "spiritueux", label: "Whiskys & Spiritueux" },
  { id: "glace", label: "Cocktails Glacés" },
  { id: "soft", label: "Softs" },
  { id: "vin_biere", label: "Vins & Bières" },
];

export default function CocktailMenu() {
  const [items, setItems] = useState([]);
  const [snacks, setSnacks] = useState([]);
  const [cat, setCat] = useState("all");
  const [posterOpen, setPosterOpen] = useState(false);
  const [posterUrl, setPosterUrl] = useState(MENU_POSTER_FALLBACK);

  useEffect(() => {
    api.get("/cocktails").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/snacks").then(({ data }) => setSnacks(data)).catch(() => {});
    // Poster image is CMS-editable via /admin/contenu — fall back to the
    // hardcoded asset if the endpoint / key is missing.
    api.get("/content")
      .then(({ data }) => {
        if (data?.carte_poster_image) setPosterUrl(data.carte_poster_image);
      })
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => cat === "all" ? items : items.filter((i) => i.category === cat), [items, cat]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="menu-page">
      <div className="mb-12 max-w-3xl">
        <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">La carte</div>
        <h1 className="font-display text-5xl lg:text-6xl mb-4">Cocktails de la maison</h1>
        <p className="text-muted-foreground leading-relaxed">
          Mocktails, créations au calvados, cocktails au rhum arrangés, planches de dégustation et cocktails glacés.
          Une sélection signature imaginée autour de spiritueux d'exception et d'ingrédients normands.
        </p>
      </div>

      {/* Hero poster of the official menu */}
      <button
        type="button"
        onClick={() => setPosterOpen(true)}
        className="group block w-full rounded-2xl overflow-hidden border border-border bg-card mb-12 relative"
        data-testid="menu-poster-cta"
        aria-label="Voir la carte officielle en grand"
      >
        <img src={posterUrl} alt="Carte officielle Saveurs Normandes" className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-700" />
        <div className="absolute top-4 right-4 bg-black/60 backdrop-blur rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-primary border border-primary/30">
          Cliquez pour agrandir
        </div>
      </button>

      {/* Full-screen poster viewer */}
      {posterOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur flex items-center justify-center p-4 lg:p-10 cursor-zoom-out"
          onClick={() => setPosterOpen(false)}
          data-testid="menu-poster-modal"
        >
          <img src={posterUrl} alt="Carte officielle Saveurs Normandes" className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
          <button
            type="button"
            onClick={() => setPosterOpen(false)}
            className="absolute top-4 right-4 rounded-full bg-card border border-border w-10 h-10 flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-10" data-testid="menu-filters">
        {CATS.map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.id)}
            className={`px-5 py-2 text-sm rounded-full border transition-colors ${
              cat === c.id ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`menu-filter-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
        {filtered.map((c) => {
          const isGrandOr = c.name.includes("Grand Or");
          const isArgent = c.name.includes("Argent");
          const awarded = isGrandOr || isArgent;
          const displayName = awarded ? c.name.split("—")[0].trim() : c.name;
          return (
            <div key={c.id} className={`flex justify-between items-start gap-4 border-b ${awarded ? "border-primary/30" : "border-border"} pb-5`} data-testid={`menu-item-${c.id}`}>
              {c.image ? (
                <img
                  src={c.image}
                  alt={displayName}
                  loading="lazy"
                  className="w-20 h-20 rounded-lg object-cover border border-border shrink-0"
                  data-testid={`menu-thumb-${c.id}`}
                />
              ) : (
                <div className="w-20 h-20 rounded-lg shrink-0 bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 flex items-center justify-center text-primary font-display text-2xl">
                  {displayName.charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <h3 className="font-display text-xl">{displayName}</h3>
                  {isGrandOr && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-primary border border-primary/50 rounded-full px-2.5 py-1" data-testid="badge-grand-or">
                      <Award className="w-3 h-3" strokeWidth={2} /> Grand Or 2026
                    </span>
                  )}
                  {isArgent && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-300 border border-zinc-400/40 rounded-full px-2.5 py-1" data-testid="badge-argent">
                      <Award className="w-3 h-3" strokeWidth={2} /> Argent 2026
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{c.description}</p>
              </div>
              <div className="font-display text-xl gold-text whitespace-nowrap">{c.price.toFixed(2)} €</div>
            </div>
          );
        })}
      </div>

      {snacks.length > 0 && (
        <div className="mt-24" data-testid="menu-snacks-section">
          <h2 className="font-display text-4xl mb-3">Snacking & Planches</h2>
          <p className="text-sm text-muted-foreground mb-8">
            Snacking disponible vendredi et samedi uniquement · Planche charcuterie & fromage sur réservation.
          </p>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
            {snacks.map((s) => (
              <div key={s.id} className="flex justify-between items-start gap-6 border-b border-border pb-5">
                <div>
                  <h3 className="font-display text-xl mb-1">{s.name}</h3>
                  <p className="text-sm text-muted-foreground">{s.description}</p>
                </div>
                <div className="font-display text-xl gold-text whitespace-nowrap">{s.price.toFixed(2)} €</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
